/**
 * listingsService.ts — Cliente de los endpoints REST del flujo
 * Business Listings + zonas administrativas para el frontend MapeoData.
 *
 * **Backend canónico**: `/api/v1/admin-zones/{list,search}` +
 * `/api/v1/listings/{estimate,search,jobs,:place_id}` (DEC-010 versionado).
 *
 * **Patrón de diseño** (T-010 sesión 3 del 2026-05-12):
 *   1. Tipos canónicos en paridad con backend domain
 *      (`admin_zone.ts` + `search_query.ts`). Si el backend cambia,
 *      este service se actualiza también — el frontend NO inventa
 *      shapes propios (DEC-016 datos empíricos, nunca inventar).
 *   2. Errores tipados `ListingsError` con `code` discriminante para
 *      que las páginas/componentes decidan UX (auth → redirect login,
 *      validation → toast, server → banner reintento).
 *   3. **Cache TTL 30 min para `listAdminZones`** (decisión J heredada
 *      de T-BLS-UI-FIX en frontend Metriclave) — las listas país/región/
 *      provincia/municipios apenas cambian; caché en memoria reduce
 *      latencia + carga backend. `invalidateAdminZonesListCache(params)`
 *      invalida selectivamente cuando cambia el contexto padre (e.g.
 *      cambio de país invalida regiones de país anterior).
 *   4. **Cero menciones del proveedor de datos** (DEC-025) — los
 *      mensajes de error son genéricos user-facing.
 *
 * **NO depende del seedeo de `mp_admin_reference_points`**: el service
 * es contrato puro. Si la BBDD está vacía, las queries devuelven
 * `items: []` (válido, el componente UI muestra "Sin clasificar"). Si
 * la BBDD tiene datos seedeados, devuelve los items reales.
 *
 * @module services/listingsService
 */

import { api, ApiException } from '@/lib/api'

// ============================================================================
// TIPOS CANÓNICOS — paridad con backend domain (`business_listings/admin_zone.ts`)
// ============================================================================

/** Nivel administrativo de una zona. Paridad con backend. */
export type AdminLevel = 'country' | 'region' | 'province' | 'municipality'

/**
 * Resultado de búsqueda/listado de zonas administrativas.
 *
 * Campos heredados del backend `list_admin_zones` + `autocomplete_zones`.
 * El campo `coverage_tier` viene de `mp_admin_reference_points.coverage_tier`
 * (1 = países foco, 2 = disponibles, 3 = cobertura limitada).
 */
export interface AdminZoneSearchResult {
  /** PK de `mp_admin_reference_points`. */
  id: number
  /** Nombre con diacríticos (NO `name_lower`). */
  name: string
  /** Versión lowercase + unaccent del nombre (no se usa en UI; backend lo
   *  expone porque su index lo necesita para fuzzy search). */
  name_lower?: string
  /** ISO 3166-1 alpha-2. */
  country_code: string
  /** Código admin nivel 1 (región/comunidad/state). Null para country/municipality directo. */
  admin_1_code: string | null
  /** Código admin nivel 2 (provincia/county). Null para country/region. */
  admin_2_code: string | null
  /** Código admin nivel 3 (municipio/comuna). Null para country/region/province. */
  admin_3_code: string | null
  /** Nivel administrativo de esta fila. */
  level: AdminLevel
  /** Área en km² (sourced de GeoBoundaries). */
  area_km2: number
  /** Radio recomendado para cubrir el centroide (km). El backend lo
   *  pre-calcula. Útil para mostrar al usuario o validar input. */
  recommended_radius_km?: number
  /** True si la zona tiene `decomposition` JSONB pre-calculada (multi-call
   *  radial requerida). False si cabe en un único círculo desde centroide. */
  has_decomposition?: boolean
  /** Número de círculos en la descomposición pre-calculada. Backend NO lo
   *  expone en list/search (solo `has_decomposition` boolean). Lo declaramos
   *  opcional para no romper si en el futuro se añade y para compatibilidad
   *  con consumidores que solo necesitan saber si > 1. */
  decomposition_count?: number
  /** Tier de cobertura GMB: 1=foco, 2=disponible, 3=limitada, null=sin clasificar. */
  coverage_tier: 1 | 2 | 3 | null
}

// ============================================================================
// ERRORES TIPADOS
// ============================================================================

/**
 * Error tipado del service. `code` permite a páginas/componentes
 * decidir UX sin parsear strings.
 *
 *   - `auth`     → 401 / 403, redirect a `/login`.
 *   - `validation` → 422 query params inválidos, toast con `details`.
 *   - `not_found` → 404 (raro en list/search, pero defensivo).
 *   - `server`   → 5xx, banner con botón reintentar.
 *   - `network`  → fetch failed (offline / CORS / DNS).
 *   - `aborted`  → AbortController abort (no error real, ignorar UX).
 */
export type ListingsErrorCode =
  | 'auth'
  | 'validation'
  | 'not_found'
  | 'server'
  | 'network'
  | 'aborted'

export class ListingsError extends Error {
  public readonly code: ListingsErrorCode
  public readonly details: string[] | null

  constructor(code: ListingsErrorCode, message: string, details: string[] | null = null) {
    super(message)
    this.name = 'ListingsError'
    this.code = code
    this.details = details
  }
}

/** Normaliza cualquier error a `ListingsError`. */
function toListingsError(e: unknown): ListingsError {
  if (e instanceof ListingsError) return e
  if (e instanceof DOMException && e.name === 'AbortError') {
    return new ListingsError('aborted', 'Request cancelado')
  }
  if (e instanceof ApiException) {
    if (e.status === 401 || e.status === 403) {
      return new ListingsError('auth', 'Sesión expirada o sin permisos', null)
    }
    if (e.status === 422) {
      // shape backend: { success:false, message, details:string[] }
      const apiErr = e.apiError as { details?: string[]; message?: string }
      return new ListingsError(
        'validation',
        apiErr.message ?? 'Datos enviados inválidos',
        apiErr.details ?? null,
      )
    }
    if (e.status === 404) {
      return new ListingsError('not_found', 'Recurso no encontrado')
    }
    if (e.status >= 500) {
      return new ListingsError('server', 'Error del servidor — reintenta')
    }
    return new ListingsError('server', e.message)
  }
  if (e instanceof TypeError) {
    // fetch network error (typeof "Failed to fetch")
    return new ListingsError('network', 'Sin conexión con el servidor')
  }
  return new ListingsError('server', (e as Error)?.message ?? 'Error desconocido')
}

// ============================================================================
// CACHE EN MEMORIA — listAdminZones (TTL 30 min, decisión J)
// ============================================================================

interface CacheEntry {
  /** Timestamp epoch ms cuando se guardó. */
  ts: number
  /** Items cacheados. */
  items: AdminZoneSearchResult[]
}

const CACHE_TTL_MS = 30 * 60 * 1000 // 30 min
const listCache = new Map<string, CacheEntry>()

/** Clave canónica del cache derivada de los filtros. */
function cacheKey(params: ListAdminZonesParams): string {
  const { level, country_code = '', admin_1_code = '', admin_2_code = '' } = params
  return `${level}|${country_code}|${admin_1_code}|${admin_2_code}`
}

/**
 * Invalida la entrada de cache del set de filtros indicado. Útil
 * cuando cambia un nivel padre (e.g. cambio de provincia invalida la
 * lista de municipios del padre anterior).
 *
 * Si `params` no se pasa, invalida TODAS las entradas (uso defensivo —
 * típicamente NO se llama sin params).
 */
export function invalidateAdminZonesListCache(params?: ListAdminZonesParams): void {
  if (!params) {
    listCache.clear()
    return
  }
  listCache.delete(cacheKey(params))
}

// ============================================================================
// API: listAdminZones — listado completo (no fuzzy)
// ============================================================================

export interface ListAdminZonesParams {
  level: AdminLevel
  /** Obligatorio si `level !== 'country'`. Validado backend con Joi. */
  country_code?: string
  /** Opcional. Filtra región padre (solo aplica a province/municipality). */
  admin_1_code?: string
  /** Opcional. Filtra provincia padre (solo aplica a municipality). */
  admin_2_code?: string
  /** Default backend: 1000. */
  limit?: number
  /** AbortSignal del componente para cancelar in-flight. */
  signal?: AbortSignal
}

/**
 * `GET /api/v1/admin-zones/list?level=...&country_code=...&limit=...`
 *
 * **Importante**: el backend NO acepta `admin_1_code` / `admin_2_code`
 * como query params (Joi solo expone `level` + `country_code` + `limit`).
 * El filtrado por padre se hace **client-side** sobre el set completo
 * devuelto. Esto es aceptable porque las cardinalidades son manejables
 * (regiones ~50/país, provincias ~50/región, municipios ~5-200/provincia).
 *
 * Cache TTL 30 min por `(level, country_code, admin_1_code, admin_2_code)`.
 */
export async function listAdminZones(
  params: ListAdminZonesParams,
): Promise<AdminZoneSearchResult[]> {
  const key = cacheKey(params)
  const cached = listCache.get(key)
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.items
  }

  // Backend acepta admin_1_code y admin_2_code como query params (Joi
  // schema verificado empíricamente — bug detectado en smoke browser
  // real del CEO 2026-05-12 sesión 3: A Coruña devolvía solo subset
  // alfabético porque mi implementación inicial limit=1000 sin pasar
  // admin_2_code recortaba arbitrariamente. Fix: enviar códigos padre
  // al backend para que filtre server-side. limit max backend = 5000.
  try {
    const resp = await api<{ items: AdminZoneSearchResult[] }>('/admin-zones/list', {
      method: 'GET',
      query: {
        level: params.level,
        country_code: params.country_code,
        admin_1_code: params.admin_1_code,
        admin_2_code: params.admin_2_code,
        limit: params.limit ?? 5000,
      },
      signal: params.signal,
    })

    let items = resp.items ?? []

    // Filtrado client-side defensivo (idempotente si el backend ya
    // filtra) — protege contra cambios futuros del backend que devuelvan
    // padres no solicitados, mantiene paridad funcional T-BLS-UI-FIX-V3.
    if (params.admin_1_code) {
      items = items.filter(
        z => z.admin_1_code === params.admin_1_code,
      )
    }
    if (params.admin_2_code) {
      items = items.filter(
        z => z.admin_2_code === params.admin_2_code,
      )
    }

    // Cache positivo (incluye filtrado por padre).
    listCache.set(key, { ts: Date.now(), items })

    return items
  } catch (e) {
    throw toListingsError(e)
  }
}

// ============================================================================
// API: searchAdminZones — autocomplete fuzzy (T-BLS-ENDPOINT)
// ============================================================================

export interface SearchAdminZonesParams {
  /** Query string fuzzy. Mínimo 2 chars (validado backend). */
  q: string
  /** Filtro opcional por país. */
  country_code?: string
  /** Filtro opcional por nivel administrativo. */
  level?: AdminLevel
  /** Default backend: 20. */
  limit?: number
  /** AbortSignal del componente para cancelar in-flight. */
  signal?: AbortSignal
}

/**
 * `GET /api/v1/admin-zones/search?q=...&country_code=...&level=...&limit=...`
 *
 * Búsqueda fuzzy tolerante a diacríticos (case-insensitive, backend usa
 * `name_lower` con extensión `unaccent`). Usado por el nivel
 * `municipality` del AdminZonePicker cuando la cardinalidad es alta y
 * el dropdown estático no es viable (ES >8000 municipios).
 *
 * **NO cacheado** — cada keystroke es una query única.
 */
export async function searchAdminZones(
  params: SearchAdminZonesParams,
): Promise<AdminZoneSearchResult[]> {
  try {
    const resp = await api<{ items: AdminZoneSearchResult[] }>('/admin-zones/search', {
      method: 'GET',
      query: {
        q: params.q,
        country_code: params.country_code,
        level: params.level,
        limit: params.limit ?? 20,
      },
      signal: params.signal,
    })
    return resp.items ?? []
  } catch (e) {
    throw toListingsError(e)
  }
}

// ============================================================================
// T-012 — Tipos canónicos de SearchQuery (paridad backend `domain/business_listings/search_query.ts`)
// ============================================================================

export interface ZoneAdminQuery {
  kind: 'admin_zone'
  country_code: string
  level: AdminLevel
  name: string
}

export interface ZoneFreeCircle {
  kind: 'free_circle'
  center_lat: number
  center_lng: number
  radius_km: number
}

export type ZoneQuery = ZoneAdminQuery | ZoneFreeCircle

export type IsClaimedFilter = 'any' | true | false

export interface SearchFilters {
  is_claimed?: IsClaimedFilter
  rating_min?: number | null
  last_updated_after?: string | null
  attributes_required?: string[] | null
  exclude_closed_forever?: boolean | null
}

export interface SearchQuery {
  /** PK de mp_admin_reference_points. XOR con zone_query. */
  zone_id?: number
  /** Forma alternativa con descripción de zona. XOR con zone_id. */
  zone_query?: ZoneQuery
  /** Slugs GMB. Mínimo 1, recomendado ≤5. */
  categories: string[]
  /** Filtros opcionales. */
  filters?: SearchFilters
  /** Cap del job. Si null/omitido, el plan del usuario lo decide. */
  limit_total?: number | null
}

// ============================================================================
// T-012 — Shapes response del backend
// ============================================================================

/**
 * Response de POST `/api/v1/listings/estimate`. Shape T-BLS-UI-FIX-V3
 * Bug I (16 keys). El frontend usa esto para renderizar EstimatorCard
 * con barra de progreso + modal confirm + bloqueo exceeds_max.
 *
 * NUNCA contiene `cost_usd` (DEC-025 ocultación proveedor).
 */
export interface EstimateResponse {
  /** Estimación total de listings encontrables en zona+categoría. */
  total_count_estimate: number
  /** Cuántos leads consumiría del cupo del usuario. */
  leads_to_consume: number
  /** Cupo restante tras consumir. */
  leads_remaining_after: number
  /** Si el cache parcial cubre parte de la extracción. */
  cache_hit_partial: boolean
  /** True si cabe en el cupo actual. */
  fits_in_quota: boolean
  /** Código quota: 'ok' | 'requires_confirm' | 'requires_2fa' | 'monthly_exhausted' | 'daily_exhausted' | 'job_exceeds_max'. */
  quota_code: string
  /** Mensaje user-facing neutro (DEC-025). */
  quota_message: string
  /** Threshold disparado, si aplica ('confirm' | '2fa' | null). */
  threshold_triggered: string | null
  /** Cupo total mensual del plan. */
  monthly_leads_quota: number
  /** Cupo restante hoy (cap diario DEC-024 capa 2). */
  leads_remaining_today: number
  /** Estimación de tiempo de ejecución en segundos. */
  estimated_seconds: number
  /** Plan del usuario ('free' | 'starter' | 'pro'). */
  plan_code: string
  /** True si requiere modal confirm (DEC-024 capa 3). */
  requires_confirmation: boolean
  /** Threshold del plan (decisión informativa al usuario). */
  confirm_threshold: number | null
  /** True si el job excede `single_job_max_leads` del plan. */
  exceeds_max: boolean
  /** Cap max del plan (informativo para UX bloqueo). */
  single_job_max: number | null
}

/** Job devuelto por `/jobs/:id` y por `search` (con `to_public_search_job`).
 *  Paridad con shape real `mp_search_jobs` post-strip de `cost_usd_*` (DEC-025).
 *  Verificado empíricamente con job real `eac49942-...` del CEO 2026-05-12. */
export interface SearchJob {
  id: string
  user_id: string
  /** Estados reales backend: 'pending'|'running'|'completed'|'failed'|'canceled'.
   *  NB: 'completed' (no 'succeeded'). */
  status: 'pending' | 'running' | 'completed' | 'failed' | 'canceled'
  /** Echo de SearchQuery + estimated_leads que envió el cliente. */
  query: {
    zone_id?: number
    zone_query?: ZoneQuery
    categories: string[]
    estimated_leads?: number
    filters?: SearchFilters | null
    limit_total?: number | null
  } | null
  /** Pre-cálculo backend de círculos a llamar (DataForSEO multi-call). */
  resolved_decomposition: Array<{ lat: number; lng: number; radius_km: number }> | null
  total_count_estimate: number | null
  total_count_actual: number | null
  calls_planned: number | null
  calls_executed: number | null
  calls_cache_hit: number | null
  /** Progreso 0..100 (porcentaje, NO decimal 0..1). */
  progress_percent: number
  result_listings_count: number
  result_dedup_dropped: number | null
  /** Mensaje user-facing si falló. */
  error: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
}

/** Listing individual — paridad con shape REAL de `mp_business_listings`
 *  verificado empíricamente 2026-05-12 sesión 3 post-smoke browser CEO
 *  (job `eac49942-...`). 43 columnas en tabla; este tipo expone las que
 *  el frontend USA. Campos `dataforseo_*`, `our_*`, `enrichment_layer_status`
 *  los devuelve el backend pero NO los consumimos (DEC-025 leak pendiente
 *  de DTO público backend — deuda T-DEC025-LISTINGS-DTO registrada en backlog).
 *
 *  `location` viene como WKB hex (PostGIS); para extraer lat/lng habría
 *  que parsear el binary o pedir al backend que exponga `lat`/`lng`
 *  numéricos. Por ahora omitimos icono "Ver en mapa". */
export interface BusinessListing {
  /** place_id GMB. Único, lo usamos como key React. */
  place_id: string
  /** Nombre del negocio. */
  title: string
  /** Categoría primaria GMB (slug en inglés, e.g. "lawyer"). */
  category_primary: string | null
  /** Categorías adicionales (lista de slugs). */
  category_ids: string[] | null
  /** Listas de categorías adicionales (compatibilidad). */
  additional_categories: string[] | null
  /** Dirección completa formateada. */
  address_full: string | null
  /** Componentes desglosados (zip, city, region, address). */
  address_components: {
    zip?: string | null
    city?: string | null
    region?: string | null
    address?: string | null
  } | null
  /** Teléfono internacional primario. */
  phone: string | null
  /** Teléfonos adicionales. */
  phones_extra: Array<{ value: string; source?: string }> | null
  /** Email primario (si scraped). */
  email_primary: string | null
  /** Emails adicionales. */
  emails_extra: Array<{ value: string; source?: string }> | null
  /** URL del website del negocio. */
  url: string | null
  /** Dominio extraído de la URL. */
  domain: string | null
  /** URL de Google Maps al perfil GMB. */
  check_url: string | null
  /** Rating 1-5. */
  rating_value: number | null
  /** Nº reviews que componen el rating. */
  rating_votes_count: number | null
  /** Distribución 1-5 estrellas. */
  rating_distribution: Record<string, number> | null
  /** Estado actual GMB (closed_temporarily, closed_forever, null). */
  current_status: string | null
  /** Atributos GMB (estructura compleja con available_attributes + unavailable_attributes). */
  attributes: {
    available_attributes?: Record<string, string[]>
    unavailable_attributes?: Record<string, string[]>
  } | null
  /** True si el listing está claimed (perfil activo). */
  is_claimed: boolean | null
  /** CID Google interno. */
  cid: string | null
  /** Feature ID Google. */
  feature_id: string | null
  /** WKB hex de la geometría POINT (lat/lng codificado). */
  location: string | null
  /** Foto principal del negocio. */
  main_image: string | null
  /** Logo del negocio. */
  logo: string | null
  /** Total fotos disponibles. */
  total_photos: number | null
  /** Cuándo se actualizó nuestro registro. */
  updated_at: string
}

export type SearchResponse =
  | {
      async: false
      job: SearchJob
      items: BusinessListing[]
      metadata: Record<string, unknown>
    }
  | {
      async: true
      job: SearchJob
      message: string
    }

/** Response de `/jobs/:id/results?format=json`. Backend devuelve
 *  `{job, offset, limit, total, items}` aplanado (NO `pagination` wrapper). */
export interface JobResultsResponse {
  job: SearchJob
  offset: number
  limit: number
  total: number
  items: BusinessListing[]
}

// ============================================================================
// T-012/QUOTA — AccountQuota DTO (GET /api/v1/account/quota)
// ============================================================================

export interface AccountQuota {
  plan_code: string
  monthly_leads_quota: number
  monthly_leads_consumed: number
  monthly_leads_remaining: number
  daily_leads_consumed: number
  daily_leads_remaining: number | null
  /** ISO-8601 fin del periodo mensual actual. */
  period_end_at: string
  /** ISO-8601 próxima medianoche UTC (reset diario). */
  day_resets_at: string
}

/** `GET /api/v1/account/quota` — estado público de cupo del usuario logueado. */
export async function getAccountQuota(signal?: AbortSignal): Promise<AccountQuota> {
  try {
    return await api<AccountQuota>('/account/quota', {
      method: 'GET',
      signal,
    })
  } catch (e) {
    throw toListingsError(e)
  }
}

// ============================================================================
// T-012 — API: estimateSearch + executeSearch + getJob + getJobResults + cancelJob
// ============================================================================

export interface ExecuteSearchOptions {
  /** Si true, añade header `X-User-Confirmed: true` (DEC-024 capa 3, modal aceptado). */
  confirmed?: boolean
  /** Si true, añade header `X-User-2FA-Verified: true` (DEC-024 capa 5, NULL en MVP). */
  twoFAVerified?: boolean
  signal?: AbortSignal
}

/**
 * `POST /api/v1/listings/estimate`
 *
 * Pre-flight obligatorio antes de `executeSearch`. Devuelve cuántos
 * leads habrá + si fits_in_quota + si requires_confirmation (DEC-024
 * capa 3). El frontend usa el `leads_to_consume` devuelto al llamar
 * `executeSearch({estimated_leads: ...})`.
 */
export async function estimateSearch(
  query: SearchQuery,
  signal?: AbortSignal,
): Promise<EstimateResponse> {
  try {
    return await api<EstimateResponse>('/listings/estimate', {
      method: 'POST',
      body: query,
      signal,
    })
  } catch (e) {
    throw toListingsError(e)
  }
}

/**
 * `POST /api/v1/listings/search`
 *
 * Lanza extracción real. Body incluye `estimated_leads` del estimate
 * previo (require_quota middleware preflight). Headers `X-User-Confirmed`
 * / `X-User-2FA-Verified` si el modal correspondiente fue aceptado.
 *
 * Response sync (≤500 leads): `{async: false, job, items, metadata}`.
 * Response async (>500): `{async: true, job (status=pending), message}`.
 */
export async function executeSearch(
  query: SearchQuery,
  estimatedLeads: number,
  options: ExecuteSearchOptions = {},
): Promise<SearchResponse> {
  const headers: Record<string, string> = {}
  if (options.confirmed) headers['X-User-Confirmed'] = 'true'
  if (options.twoFAVerified) headers['X-User-2FA-Verified'] = 'true'

  try {
    return await api<SearchResponse>('/listings/search', {
      method: 'POST',
      body: { ...query, estimated_leads: estimatedLeads },
      headers,
      signal: options.signal,
    })
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `GET /api/v1/listings/jobs/:id` — polling del estado del job. */
export async function getJob(
  jobId: string,
  signal?: AbortSignal,
): Promise<SearchJob> {
  try {
    const resp = await api<{ job: SearchJob }>(
      `/listings/jobs/${encodeURIComponent(jobId)}`,
      { method: 'GET', signal },
    )
    return resp.job
  } catch (e) {
    throw toListingsError(e)
  }
}

export interface GetJobResultsParams {
  offset?: number
  limit?: number
  /** 'json' (default) | 'csv' | 'xlsx'. */
  format?: 'json' | 'csv' | 'xlsx'
  signal?: AbortSignal
}

/**
 * `GET /api/v1/listings/jobs/:id/results?offset=&limit=&format=`
 *
 * Devuelve paginación de resultados. format='csv'|'xlsx' devuelve
 * `Content-Disposition: attachment` para descarga directa — desde el
 * frontend conviene abrir en una ventana o construir <a download>.
 *
 * Para uso en UI tabla, usar `format='json'` (default).
 */
export async function getJobResults(
  jobId: string,
  params: GetJobResultsParams = {},
): Promise<JobResultsResponse> {
  try {
    return await api<JobResultsResponse>(
      `/listings/jobs/${encodeURIComponent(jobId)}/results`,
      {
        method: 'GET',
        query: {
          offset: params.offset ?? 0,
          limit: params.limit ?? 100,
          format: params.format ?? 'json',
        },
        signal: params.signal,
      },
    )
  } catch (e) {
    throw toListingsError(e)
  }
}

/**
 * URL absoluta para descarga directa (`<a href={...} download>`).
 * Para CSV/XLSX el browser maneja el Content-Disposition. Incluye el
 * Bearer JWT como query param NO — el backend lee `Authorization`
 * header. La descarga directa desde browser requiere usar fetch +
 * Blob para inyectar el header (helper aparte si se necesita UX
 * de descarga sin abrir ventana).
 */
export function getJobResultsDownloadUrl(
  jobId: string,
  format: 'csv' | 'xlsx',
): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || ''
  return `${base}/api/v1/listings/jobs/${encodeURIComponent(jobId)}/results?format=${format}`
}

// ============================================================================
// T-016 — Bandeja del usuario (inbox) + listado de jobs
// ============================================================================

/**
 * Filtros opcionales del endpoint `GET /api/v1/listings/inbox`. Todos
 * opcionales — sin filtros, el backend devuelve la bandeja completa.
 */
export interface InboxFilters {
  job_id?: string
  category_primary?: string
  country_code?: string
  has_email?: boolean
  has_phone?: boolean
  has_url?: boolean
  is_claimed?: boolean
  rating_min?: number
  date_from?: string
  date_to?: string
  q?: string
}

/** Columnas por las que el backend permite ordenar la bandeja (whitelist EXACTA;
 *  un valor fuera de esta lista da 422 → no se envía). T-029. */
export const INBOX_ORDER_BY = [
  'title',
  'category_primary',
  'city',
  'rating_value',
  'rating_votes_count',
  'is_claimed',
  'first_extracted_at',
  'jobs_count',
] as const
export type InboxOrderBy = (typeof INBOX_ORDER_BY)[number]
export type InboxOrderDir = 'asc' | 'desc'

/**
 * Una fila de la bandeja: el listing + metadata de extracción del
 * usuario (primer job que lo trajo + cuántas veces ha aparecido en sus
 * búsquedas). Pre-deduplicado por place_id en backend (vista
 * v_mp_user_inbox).
 *
 * Shape completo de columnas disponibles para mostrar/ocultar en la
 * tabla — el CEO eligió "mostrar todo" como valor diferencial de
 * MapeoData (cantidad de información disponible).
 *
 * Campos sensibles (`dataforseo_*`, `our_*`, etc.) strippeados por
 * `to_public_listing` en backend (T-015).
 */
export interface InboxItem {
  /** Identificador único Google Maps Place. */
  place_id: string
  /** Nombre del negocio. */
  title: string
  /** Nombre original sin romanizar (CJK, árabe…). Null si no aplica. */
  original_title: string | null
  /** Descripción del negocio en GMB. Puede ser larga. */
  description: string | null
  /** Categoría primaria (slug GMB en inglés). */
  category_primary: string | null
  /** Todas las categorías GMB (hasta 10 slugs). */
  category_ids: string[] | null
  /** Etiquetas legibles ("Pizza Restaurant", etc.). */
  additional_categories: string[] | null
  /** Dirección completa formateada. */
  address_full: string | null
  /** Componentes desglosados: zip, city, region, country_code. */
  address_components: {
    zip?: string | null
    city?: string | null
    region?: string | null
    country_code?: string | null
    address?: string | null
  } | null
  /** Teléfono primario internacional. */
  phone: string | null
  /** Teléfonos adicionales (estructura JSONB del proveedor). */
  phones_extra: unknown | null
  /** URL del website. */
  url: string | null
  /** Dominio extraído de la URL. */
  domain: string | null
  /** Email primario scrapeado del website. */
  email_primary: string | null
  /** Emails adicionales scrapeados. */
  emails_extra: string[] | null
  /** Logo GMB. */
  logo: string | null
  /** Foto principal GMB. */
  main_image: string | null
  /** Total de fotos en GMB. */
  total_photos: number | null
  /** True si el perfil está reclamado por el negocio. */
  is_claimed: boolean | null
  /** Rating 0..5. */
  rating_value: number | null
  /** Número de reseñas que componen el rating. */
  rating_votes_count: number | null
  /** Distribución de estrellas por nivel. */
  rating_distribution: Record<string, number> | null
  /** Rango de precios ($, $$, $$$, $$$$). */
  price_level: string | null
  /** Estado actual: 'open' | 'close' | 'closed_forever' | null. */
  current_status: string | null
  /** Horario semanal estructurado (JSONB). */
  work_hours: unknown | null
  /** Atributos GMB (accesibilidad, identidad, parking, etc.). */
  attributes: unknown | null
  /** Topics extraídos de reseñas ({concept: frequency}). */
  place_topics: unknown | null
  /** URL al perfil en Google Maps. */
  check_url: string | null
  /** CID interno Google. */
  cid: string | null
  /** Feature ID Google. */
  feature_id: string | null
  /** Latitud WGS84 en grados (contrato aditivo backend). Null si no hay coords. */
  latitude: number | null
  /** Longitud WGS84 en grados (contrato aditivo backend). Null si no hay coords. */
  longitude: number | null
  /** Primera vez que este lead entró en la bandeja del usuario. */
  first_extracted_at: string
  /** Job que lo trajo por primera vez. */
  first_job_id: string
  /** Cuántas búsquedas del usuario lo han devuelto (≥1). */
  jobs_count: number
}

export interface InboxResponse {
  offset: number
  limit: number
  total: number
  items: InboxItem[]
  /** Eco del orden aplicado por el backend (T-029). */
  order_by?: InboxOrderBy
  order_dir?: InboxOrderDir
}

/**
 * `GET /api/v1/listings/inbox?filtros&offset=&limit=`
 *
 * Bandeja acumulada del usuario pre-deduplicada por place_id. Filtros
 * combinables. Si un filtro no se quiere aplicar, no se incluye.
 *
 * Para `has_email/has_phone/has_url/is_claimed`: pasar true/false según
 * se quiera. Si no se pasa, no filtra.
 */
export async function getInbox(
  filters: InboxFilters = {},
  options: {
    offset?: number
    limit?: number
    order_by?: InboxOrderBy
    order_dir?: InboxOrderDir
    signal?: AbortSignal
  } = {},
): Promise<InboxResponse> {
  // Serializar query params descartando undefined.
  const query: Record<string, string | number | boolean> = {}
  if (filters.job_id) query.job_id = filters.job_id
  if (filters.category_primary) query.category_primary = filters.category_primary
  if (filters.country_code) query.country_code = filters.country_code
  if (filters.has_email !== undefined) query.has_email = filters.has_email
  if (filters.has_phone !== undefined) query.has_phone = filters.has_phone
  if (filters.has_url !== undefined) query.has_url = filters.has_url
  if (filters.is_claimed !== undefined) query.is_claimed = filters.is_claimed
  if (typeof filters.rating_min === 'number') query.rating_min = filters.rating_min
  if (filters.date_from) query.date_from = filters.date_from
  if (filters.date_to) query.date_to = filters.date_to
  if (filters.q && filters.q.trim().length > 0) query.q = filters.q.trim()
  // Orden server-side (T-029): solo se envía si hay columna whitelisted.
  if (options.order_by && (INBOX_ORDER_BY as readonly string[]).includes(options.order_by)) {
    query.order_by = options.order_by
    query.order_dir = options.order_dir === 'asc' ? 'asc' : 'desc'
  }
  query.offset = options.offset ?? 0
  query.limit = options.limit ?? 50

  try {
    return await api<InboxResponse>('/listings/inbox', {
      method: 'GET',
      query,
      signal: options.signal,
    })
  } catch (e) {
    throw toListingsError(e)
  }
}

/** Cap del dataset del mapa (el backend trunca a este máximo). */
const INBOX_MAP_CAP = 10000

/**
 * `GET /api/v1/listings/inbox/map` — TODOS los leads del usuario (sin paginar,
 * cap {@link INBOX_MAP_CAP}) aplicando los filtros activos, para alimentar el
 * mapa de Búsqueda (la tabla sigue paginada con `getInbox`). Mismo shape de
 * item que el inbox (`InboxItem`, ya con latitude/longitude).
 *
 * Defensivo con la forma de la respuesta (lección bug Plan): acepta tanto un
 * array crudo `InboxItem[]` como `{ items: InboxItem[] }` y normaliza a array.
 */
export async function getInboxMap(
  filters: InboxFilters = {},
  signal?: AbortSignal,
): Promise<InboxItem[]> {
  const query: Record<string, string | number | boolean> = {}
  if (filters.job_id) query.job_id = filters.job_id
  if (filters.category_primary) query.category_primary = filters.category_primary
  if (filters.country_code) query.country_code = filters.country_code
  if (filters.has_email !== undefined) query.has_email = filters.has_email
  if (filters.has_phone !== undefined) query.has_phone = filters.has_phone
  if (filters.has_url !== undefined) query.has_url = filters.has_url
  if (filters.is_claimed !== undefined) query.is_claimed = filters.is_claimed
  if (typeof filters.rating_min === 'number') query.rating_min = filters.rating_min
  if (filters.date_from) query.date_from = filters.date_from
  if (filters.date_to) query.date_to = filters.date_to
  if (filters.q && filters.q.trim().length > 0) query.q = filters.q.trim()
  query.limit = INBOX_MAP_CAP

  try {
    const r = await api<InboxItem[] | InboxResponse>('/listings/inbox/map', {
      method: 'GET',
      query,
      signal,
    })
    if (Array.isArray(r)) return r
    return r?.items ?? []
  } catch (e) {
    throw toListingsError(e)
  }
}

/**
 * Resumen de un job del usuario, devuelto por el listado. Sin
 * `cost_usd_*` (DEC-025). No incluye `resolved_decomposition` ni `error`
 * — el selector solo necesita lo mínimo para una etiqueta legible.
 */
export interface UserJobSummary {
  id: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'canceled'
  query: {
    zone_id?: number
    zone_query?: ZoneQuery
    categories?: string[]
  } | null
  result_listings_count: number | null
  total_count_estimate: number | null
  created_at: string
  completed_at: string | null
  /** Campos derivados en la RAÍZ del item (T-022, backend). El widget/selector
   *  los usa para mostrar zona, categorías y nivel sin depender de `query`. */
  /** Nombre legible de la zona (ej. "Ferrol"). Null en jobs por coordenadas. */
  zone_label?: string | null
  /** Nivel administrativo de la zona ('country'|'region'|'province'|'municipality'). */
  zone_level?: string | null
  /** Slugs GMB de las categorías del job (en RAÍZ; pasar por categoryLabel). */
  categories?: string[] | null
}

export interface UserJobsResponse {
  offset: number
  limit: number
  total: number
  items: UserJobSummary[]
}

/**
 * `GET /api/v1/listings/jobs?offset=&limit=`
 *
 * Lista las búsquedas pasadas del usuario, más recientes primero.
 * Alimenta el selector "filtrar por búsqueda concreta" del frontend.
 */
export async function listMyJobs(
  options: { offset?: number; limit?: number; signal?: AbortSignal } = {},
): Promise<UserJobsResponse> {
  try {
    return await api<UserJobsResponse>('/listings/jobs', {
      method: 'GET',
      query: {
        offset: options.offset ?? 0,
        limit: options.limit ?? 50,
      },
      signal: options.signal,
    })
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `POST /api/v1/listings/jobs/:id/cancel`. */
export async function cancelJob(
  jobId: string,
  signal?: AbortSignal,
): Promise<{ job: SearchJob }> {
  try {
    return await api<{ job: SearchJob }>(
      `/listings/jobs/${encodeURIComponent(jobId)}/cancel`,
      { method: 'POST', signal },
    )
  } catch (e) {
    throw toListingsError(e)
  }
}

// ============================================================================
// PIPELINE / KANBAN (KANBAN-PIPELINE) — /api/v1/listings/pipeline
// ============================================================================

/** Estados del embudo (coinciden con el backend). */
export const PIPELINE_STAGES = ['nuevo', 'contactado', 'propuesta', 'cerrado'] as const
export type PipelineStage = (typeof PIPELINE_STAGES)[number]

/** Criterios de orden del kanban (paridad backend `PIPELINE_ORDER_BY`). */
export const PIPELINE_ORDER_BY = ['next_action', 'added', 'score'] as const
export type PipelineOrderBy = (typeof PIPELINE_ORDER_BY)[number]

/** Estado de "calor" del próximo paso (lo deriva el backend). */
export type NextStepStatus = 'overdue' | 'today' | 'no_next_step' | 'future'

/** Límite del texto del próximo paso (paridad backend `NEXT_STEP_TEXT_MAX`). */
export const NEXT_STEP_TEXT_MAX = 280

/**
 * Tarjeta del pipeline: los datos del negocio van EN LA RAÍZ (igual que un
 * `InboxItem`) + el `stage` del embudo + el seguimiento CRM (próximo paso) +
 * timestamps del pipeline + los booleanos `has_*` que envía el backend. Es
 * compatible con `InboxItem` para reutilizar `opportunityScore` / `leadSignals`.
 */
export interface PipelineCard extends InboxItem {
  stage: PipelineStage
  /** Texto del próximo paso (ej. "Llamar para auditoría"). Null si no hay. */
  next_step_text: string | null
  /** Fecha de follow-up 'YYYY-MM-DD'. Null si no hay próximo paso. */
  next_step_due: string | null
  /** Estado derivado del próximo paso para el aviso de la tarjeta. */
  next_step_status: NextStepStatus
  /** Timestamps del PIPELINE (no del negocio). */
  created_at: string
  updated_at: string
  has_email?: boolean
  has_phone?: boolean
  has_url?: boolean
}

export interface PipelineResponse {
  total: number
  order_by?: PipelineOrderBy
  items: PipelineCard[]
}

/**
 * Normaliza un item crudo de `GET /pipeline` a `PipelineCard` con TODOS los
 * campos de `InboxItem` presentes (rellena con null/defaults los que la tarjeta
 * del pipeline no traiga — p.ej. `jobs_count`, `first_extracted_at`, que son de
 * la vista de bandeja y NO del row del negocio). Defensivo: el shape real manda,
 * no se asume (lección del bug de Plan).
 */
export function normalizePipelineCard(raw: Record<string, unknown>): PipelineCard {
  const r = raw as Partial<InboxItem> & {
    place_id: string
    stage: PipelineStage
    next_step_text?: string | null
    next_step_due?: string | null
    next_step_status?: NextStepStatus
    created_at?: string
    updated_at?: string
    has_email?: boolean
    has_phone?: boolean
    has_url?: boolean
  }
  return {
    place_id: r.place_id,
    title: r.title ?? '—',
    original_title: r.original_title ?? null,
    description: r.description ?? null,
    category_primary: r.category_primary ?? null,
    category_ids: r.category_ids ?? null,
    additional_categories: r.additional_categories ?? null,
    address_full: r.address_full ?? null,
    address_components: r.address_components ?? null,
    phone: r.phone ?? null,
    phones_extra: r.phones_extra ?? null,
    url: r.url ?? null,
    domain: r.domain ?? null,
    email_primary: r.email_primary ?? null,
    emails_extra: r.emails_extra ?? null,
    logo: r.logo ?? null,
    main_image: r.main_image ?? null,
    total_photos: r.total_photos ?? null,
    is_claimed: r.is_claimed ?? null,
    rating_value: r.rating_value ?? null,
    rating_votes_count: r.rating_votes_count ?? null,
    rating_distribution: r.rating_distribution ?? null,
    price_level: r.price_level ?? null,
    current_status: r.current_status ?? null,
    work_hours: r.work_hours ?? null,
    attributes: r.attributes ?? null,
    place_topics: r.place_topics ?? null,
    check_url: r.check_url ?? null,
    cid: r.cid ?? null,
    feature_id: r.feature_id ?? null,
    latitude: r.latitude ?? null,
    longitude: r.longitude ?? null,
    first_extracted_at: r.first_extracted_at ?? r.created_at ?? '',
    first_job_id: r.first_job_id ?? '',
    jobs_count: r.jobs_count ?? 1,
    stage: r.stage,
    next_step_text: r.next_step_text ?? null,
    next_step_due: r.next_step_due ?? null,
    next_step_status: r.next_step_status ?? 'no_next_step',
    created_at: r.created_at ?? '',
    updated_at: r.updated_at ?? '',
    has_email: r.has_email,
    has_phone: r.has_phone,
    has_url: r.has_url,
  }
}

/**
 * `GET /api/v1/listings/pipeline?order_by=...` → `{ total, order_by, items }`
 * (datos del negocio en raíz). El orden RELATIVO dentro de cada columna lo fija
 * `order_by` (default `next_action`).
 */
export async function getPipeline(
  orderBy: PipelineOrderBy = 'next_action',
  signal?: AbortSignal,
): Promise<PipelineResponse> {
  try {
    const r = await api<{ total?: number; order_by?: PipelineOrderBy; items?: Record<string, unknown>[] }>(
      '/listings/pipeline',
      { method: 'GET', query: { order_by: orderBy }, signal },
    )
    const items = (r?.items ?? []).map(normalizePipelineCard)
    return { total: r?.total ?? items.length, order_by: r?.order_by ?? orderBy, items }
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `POST /api/v1/listings/pipeline` — añade place_ids (UPSERT; sin stage no pisa). */
export async function addToPipeline(
  placeIds: string[],
  stage?: 'nuevo' | 'contactado',
  signal?: AbortSignal,
): Promise<{ success: boolean; count: number; place_ids: string[]; stage: PipelineStage }> {
  try {
    return await api('/listings/pipeline', {
      method: 'POST',
      body: stage ? { place_ids: placeIds, stage } : { place_ids: placeIds },
      signal,
    })
  } catch (e) {
    throw toListingsError(e)
  }
}

/**
 * `PATCH /api/v1/listings/pipeline/:place_id` — actualiza un subconjunto de
 * `{ stage, next_step_text, next_step_due }`. `null` en los campos de próximo
 * paso los BORRA. El cambio de stage se auto-loguea en el timeline (backend).
 */
export interface PipelineItemPatch {
  stage?: PipelineStage
  /** Texto del próximo paso; `null` borra el próximo paso. */
  next_step_text?: string | null
  /** Fecha 'YYYY-MM-DD'; `null` borra la fecha. */
  next_step_due?: string | null
}

export async function updatePipelineItem(
  placeId: string,
  patch: PipelineItemPatch,
  signal?: AbortSignal,
): Promise<{
  success: boolean
  place_id: string
  stage: PipelineStage
  next_step_text: string | null
  next_step_due: string | null
  updated_at: string
}> {
  try {
    return await api(`/listings/pipeline/${encodeURIComponent(placeId)}`, {
      method: 'PATCH',
      body: patch,
      signal,
    })
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `PATCH /api/v1/listings/pipeline/:place_id` — mover de columna (atajo). */
export async function updatePipelineStage(
  placeId: string,
  stage: PipelineStage,
  signal?: AbortSignal,
): Promise<{ success: boolean; place_id: string; stage: PipelineStage; updated_at: string }> {
  return updatePipelineItem(placeId, { stage }, signal)
}

/** `DELETE /api/v1/listings/pipeline/:place_id` — sacar del pipeline (idempotente). */
export async function removeFromPipeline(
  placeId: string,
  signal?: AbortSignal,
): Promise<{ success: boolean; place_id: string; removed: boolean }> {
  try {
    return await api(`/listings/pipeline/${encodeURIComponent(placeId)}`, {
      method: 'DELETE',
      signal,
    })
  } catch (e) {
    throw toListingsError(e)
  }
}

// ============================================================================
// TIMELINE DE ACTIVIDAD CRM (T-024) — /pipeline/:place_id/activity
// ============================================================================

/** Clases de fila del timeline (paridad backend). */
export type ActivityKind = 'note' | 'event'

/** Tipos de nota (etiqueta+icono; no cambian el flujo). */
export const ACTIVITY_TYPES = ['note', 'call', 'email', 'meeting'] as const
export type ActivityType = (typeof ACTIVITY_TYPES)[number]

/** Códigos de evento del sistema auto-logueados. */
export type EventCode = 'added_to_pipeline' | 'stage_changed'

/** Límite del cuerpo de una nota (paridad backend `NOTE_BODY_MAX`). */
export const NOTE_BODY_MAX = 4000

/**
 * Una fila del timeline tal como la proyecta el backend (`to_activity_item`).
 * `kind='note'` → nota manual editable/borrable; `kind='event'` → evento del
 * sistema (solo lectura), con `event_code` + `event_meta` ({from,to}).
 */
export interface ActivityItem {
  id: string
  place_id: string
  kind: ActivityKind
  activity_type: ActivityType | null
  body: string | null
  event_code: EventCode | null
  event_meta: { from?: string; to?: string } | Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export interface ActivityResponse {
  total: number
  items: ActivityItem[]
}

/** `GET /pipeline/:place_id/activity` → timeline (reciente arriba). */
export async function getLeadActivity(
  placeId: string,
  signal?: AbortSignal,
): Promise<ActivityResponse> {
  try {
    const r = await api<{ total?: number; items?: ActivityItem[] }>(
      `/listings/pipeline/${encodeURIComponent(placeId)}/activity`,
      { method: 'GET', signal },
    )
    const items = r?.items ?? []
    return { total: r?.total ?? items.length, items }
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `POST /pipeline/:place_id/activity` → crea una nota manual (201 → `{item}`). */
export async function addNote(
  placeId: string,
  body: string,
  activityType: ActivityType = 'note',
  signal?: AbortSignal,
): Promise<ActivityItem> {
  try {
    const r = await api<{ item: ActivityItem }>(
      `/listings/pipeline/${encodeURIComponent(placeId)}/activity`,
      { method: 'POST', body: { body, activity_type: activityType }, signal },
    )
    return r.item
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `PATCH /pipeline/:place_id/activity/:activity_id` → edita una nota. */
export async function updateNote(
  placeId: string,
  activityId: string,
  patch: { body?: string; activity_type?: ActivityType },
  signal?: AbortSignal,
): Promise<ActivityItem> {
  try {
    const r = await api<{ item: ActivityItem }>(
      `/listings/pipeline/${encodeURIComponent(placeId)}/activity/${encodeURIComponent(activityId)}`,
      { method: 'PATCH', body: patch, signal },
    )
    return r.item
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `DELETE /pipeline/:place_id/activity/:activity_id` → borra una nota. */
export async function deleteNote(
  placeId: string,
  activityId: string,
  signal?: AbortSignal,
): Promise<{ success: boolean; activity_id: string; removed: boolean }> {
  try {
    return await api(
      `/listings/pipeline/${encodeURIComponent(placeId)}/activity/${encodeURIComponent(activityId)}`,
      { method: 'DELETE', signal },
    )
  } catch (e) {
    throw toListingsError(e)
  }
}

// ============================================================================
// MÓDULO DE RESEÑAS + ÁNGULO DE VENTA — /listings/reviews/:place_id
//
// Flujo async (poll-on-read): POST dispara la extracción → el cliente hace
// polling de GET (que internamente AVANZA el job) hasta `status==='completed'`;
// entonces `items` trae las reseñas. El ÁNGULO DE VENTA (nuestro diferencial)
// se computa al completar y se lee en GET /angle.
//
// Tipado EXACTO contra el backend (rama feature/reviews-module):
//   · ReviewJob          = services/reviews_service.ts → to_job()
//   · BusinessReview     = domain/business_reviews/business_review.ts
//   · SalesAngle/Signal  = domain/business_reviews/business_review.ts
// ============================================================================

/** Estado del job de extracción de reseñas (paridad backend). */
export type ReviewJobStatus = 'pending' | 'running' | 'completed' | 'failed'

/** Job de extracción de reseñas (sin coste interno; DEC-025). */
export interface ReviewJob {
  id: string
  place_id: string
  status: ReviewJobStatus
  depth: number
  sort_by: string
  /** Total de reseñas que reporta el proveedor (null hasta conocerse). */
  reviews_count_total: number | null
  /** Cuántas reseñas se han persistido (avanza con el polling). */
  reviews_stored: number | null
  /** Mensaje de error neutro si `status==='failed'`. */
  error_message: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
}

/** Una reseña individual de Google, normalizada (paridad backend). */
export interface BusinessReview {
  review_id: string
  author_name: string | null
  author_profile_url: string | null
  author_image_url: string | null
  /** Puntuación INDIVIDUAL de esta reseña (0–5). null si no aplica. */
  rating: number | null
  /** Fecha de publicación ISO (UTC). null si el proveedor no la da. */
  review_timestamp: string | null
  /** Texto crudo del proveedor para la fecha ("2 weeks ago"). */
  time_ago: string | null
  text: string | null
  original_text: string | null
  language: string | null
  owner_answer: string | null
  owner_timestamp: string | null
  /** URLs de fotos adjuntas (Google CDN → pueden caducar). */
  photos: string[]
  local_guide: boolean | null
}

/** Código de ángulo de venta v1 (heurística backend). */
export type SalesAngleCode =
  | 'web_reservas'
  | 'visibilidad_contacto'
  | 'reputacion_resenas'
  | 'experiencia_servicio'
  | 'sin_angulo'

/** Una señal/tema detectado en las reseñas, con evidencia. */
export interface SalesAngleSignal {
  code: string
  label: string
  /** Nº de reseñas donde aparece el tema (0 en señales objetivas). */
  mentions: number
  /** De esas, cuántas son reseñas negativas. */
  in_negative: number
  /** Evidencia: fragmentos de reseña, o el enunciado del dato objetivo. */
  evidence: string[]
  /**
   * Origen de la señal (v2, OPCIONAL/no-breaking):
   *   - 'review'    → derivada de menciones en reseñas (quejas de clientes).
   *   - 'objective' → dato objetivo del negocio (p.ej. no tiene web).
   * Ausente en análisis v1 → tratar como 'review'.
   */
  kind?: 'review' | 'objective'
}

/** Ángulo de venta derivado (nuestro diferenciador). */
export interface SalesAngle {
  primary_code: SalesAngleCode
  primary_label: string
  /** Frase accionable en lenguaje de vendedor. */
  pitch: string
  signals: SalesAngleSignal[]
  reviews_analyzed: number
  negative_reviews: number
  avg_rating: number | null
  rating_votes_count: number | null
  version: number
  /** Añadidos por el service al persistir. */
  place_id: string
  computed_at: string
}

/** Sort del tablero (GET): paridad con el Joi del controlador. */
export const REVIEWS_SORT = ['newest', 'oldest', 'highest_rating', 'lowest_rating'] as const
export type ReviewsSort = (typeof REVIEWS_SORT)[number]

/** Respuesta de `GET /reviews/:place_id`. */
export interface ReviewsResponse {
  place_id: string
  job: ReviewJob | null
  offset: number
  limit: number
  total: number
  items: BusinessReview[]
}

/** Opciones de disparo de la extracción de reseñas. */
export interface RequestReviewsOptions {
  /** Profundidad (nº de reseñas a traer). Backend cap = 700, default 50. */
  depth?: number
  /** Orden de captura del proveedor. */
  sort_by?: 'newest' | 'highest_rating' | 'lowest_rating' | 'relevant'
}

/** `POST /reviews/:place_id` → dispara la extracción async (202 → `{job}`). */
export async function requestReviews(
  placeId: string,
  options: RequestReviewsOptions = {},
  signal?: AbortSignal,
): Promise<ReviewJob> {
  try {
    const body: Record<string, unknown> = {}
    if (options.depth != null) body.depth = options.depth
    if (options.sort_by) body.sort_by = options.sort_by
    const r = await api<{ success: boolean; job: ReviewJob }>(
      `/listings/reviews/${encodeURIComponent(placeId)}`,
      { method: 'POST', body, signal },
    )
    return r.job
  } catch (e) {
    throw toListingsError(e)
  }
}

/** Opciones del tablero (filtro fotos + orden + paginación). */
export interface GetReviewsOptions {
  only_with_photos?: boolean
  sort?: ReviewsSort
  offset?: number
  limit?: number
}

/**
 * `GET /reviews/:place_id` → reseñas guardadas + estado del job.
 * Poll-on-read: cada GET AVANZA el job si está 'running' (no hay worker).
 */
export async function getReviews(
  placeId: string,
  options: GetReviewsOptions = {},
  signal?: AbortSignal,
): Promise<ReviewsResponse> {
  try {
    const r = await api<Partial<ReviewsResponse>>(
      `/listings/reviews/${encodeURIComponent(placeId)}`,
      {
        method: 'GET',
        query: {
          only_with_photos: options.only_with_photos ? true : undefined,
          sort: options.sort,
          offset: options.offset,
          limit: options.limit,
        },
        signal,
      },
    )
    const items = r?.items ?? []
    return {
      place_id: r?.place_id ?? placeId,
      job: r?.job ?? null,
      offset: r?.offset ?? options.offset ?? 0,
      limit: r?.limit ?? options.limit ?? 50,
      total: r?.total ?? items.length,
      items,
    }
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `GET /reviews/:place_id/status` → estado ligero del job (poll-on-read). */
export async function getReviewsStatus(
  placeId: string,
  signal?: AbortSignal,
): Promise<ReviewJob | null> {
  try {
    const r = await api<{ place_id: string; job: ReviewJob | null }>(
      `/listings/reviews/${encodeURIComponent(placeId)}/status`,
      { method: 'GET', signal },
    )
    return r?.job ?? null
  } catch (e) {
    throw toListingsError(e)
  }
}

/** `GET /reviews/:place_id/angle` → ángulo de venta (null si aún no analizado). */
export async function getSalesAngle(
  placeId: string,
  signal?: AbortSignal,
): Promise<SalesAngle | null> {
  try {
    const r = await api<{ place_id: string; angle: SalesAngle | null }>(
      `/listings/reviews/${encodeURIComponent(placeId)}/angle`,
      { method: 'GET', signal },
    )
    return r?.angle ?? null
  } catch (e) {
    throw toListingsError(e)
  }
}
